import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdPersonAdd, MdListAlt, MdCheckCircle, MdAddCircle, MdError,
    MdCurrencyRupee, MdGroup, MdRefresh, MdSearch, MdPerson,
    MdFlipCameraIos, MdClass, MdChevronLeft, MdChevronRight,
    MdArrowForward, MdClose, MdLock, MdSchool, MdCalendarToday, MdFingerprint,
    MdToday, MdPhone, MdWarning, MdEmail, MdAccessTime, MdInfo
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const cardRadius = '12px';

// ── Reusable table cells (same as rest of app) ──
const Th = ({ children }) => (
    <th style={{
        padding: '0.85rem 1rem', textAlign: 'left', fontSize: '0.75rem',
        fontWeight: '700', color: '#718096', textTransform: 'uppercase',
        letterSpacing: '0.05em', background: '#F8FAFC', whiteSpace: 'nowrap',
        borderBottom: '1px solid #E2E8F0'
    }}>{children}</th>
);
const Td = ({ children, style }) => (
    <td style={{
        padding: '0.85rem 1rem', fontSize: '0.88rem', color: '#4A5568',
        borderBottom: '1px solid #F7FAFC', ...style
    }}>{children}</td>
);

// ── FormInput helper matching existing admission form style ──
function FormInput({ label, name, type = 'text', value, onChange, required, placeholder, readOnly }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>{label}</label>
            <input
                type={type} name={name} value={value} onChange={onChange}
                required={required} placeholder={placeholder} readOnly={readOnly}
                style={{
                    padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0',
                    width: '100%', outline: 'none', fontSize: '0.9rem', boxSizing: 'border-box',
                    background: readOnly ? '#F7FAFC' : '#FFFFFF',
                    color: readOnly ? '#718096' : '#2D3748',
                    cursor: readOnly ? 'not-allowed' : 'text'
                }}
            />
        </div>
    );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function FreeAdmissionPage() {
    const { user } = useAuth();
    const [activeView, setActiveView] = useState('form');
    const [loading, setLoading] = useState(false);
    const [freeAdmissions, setFreeAdmissions] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null); // For details modal
    const [currentPage, setCurrentPage] = useState(1);
    const [statusUpdating, setStatusUpdating] = useState(null); // id being updated
    const itemsPerPage = 10;

    // ── Camera ──
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [cameraError, setCameraError] = useState(false);
    const [facingMode, setFacingMode] = useState('environment');

    // ── Form Data ──
    const [formData, setFormData] = useState({
        student_name: '', email: '', batch: '', batch_time: '',
        drcss_no: '', aadhar_no: '', course: 'KYP',
        admission_date: new Date().toISOString().split('T')[0],
        payment_amount: '0',
        admission_type: 'free',
        center_name: '', center_address: '',
        photo: null,
    });

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                center_name: user.center_name || '',
                center_address: user.center_address || '',
            }));
        }
    }, [user]);

    // ── Fetch free admissions (only this center's) ──
    const fetchFreeAdmissions = async () => {
        setFetchLoading(true);
        try {
            const res = await api.get('/admissions?type=free&per_page=999999');
            const resData = res.data?.admissions;
            const all = Array.isArray(resData) ? resData : (resData?.data || []);
            setFreeAdmissions(all.filter(a => a.admission_type === 'free'));
        } catch (e) {
            console.error(e);
        } finally {
            setFetchLoading(false);
        }
    };

    useEffect(() => { fetchFreeAdmissions(); }, []);

    // ── Camera Helpers ──
    const startCamera = async (mode = facingMode) => {
        setCameraError(false);
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode } });
            setIsCameraOpen(true);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => { });
                }
            }, 100);
        } catch {
            if (mode === 'environment') {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    setIsCameraOpen(true);
                    setTimeout(() => {
                        if (videoRef.current) {
                            videoRef.current.srcObject = stream;
                            videoRef.current.play().catch(() => { });
                        }
                    }, 100);
                } catch { setCameraError(true); }
            } else { setCameraError(true); }
        }
    };

    const stopCamera = () => {
        setIsCameraOpen(false);
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            videoRef.current.srcObject = null;
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;
        const v = videoRef.current, c = canvasRef.current;
        c.width = v.videoWidth; c.height = v.videoHeight;
        c.getContext('2d').drawImage(v, 0, 0);
        c.toBlob(blob => {
            const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
            setFormData(prev => ({ ...prev, photo: file }));
            setCapturedImage(URL.createObjectURL(blob));
            stopCamera();
        }, 'image/jpeg', 0.85);
    };

    const retakePhoto = () => {
        setCapturedImage(null);
        setFormData(prev => ({ ...prev, photo: null }));
        startCamera();
    };

    const switchCamera = () => {
        const m = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(m); startCamera(m);
    };

    // ── Submit ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.photo) {
            setMsg('Student photo is required. Please capture or upload a photo first.');
            return;
        }
        setLoading(true); setMsg('');
        try {
            const payload = new FormData();
            Object.keys(formData).forEach(key => {
                if (key !== 'photo' && formData[key] !== null) payload.append(key, formData[key]);
            });
            if (formData.photo) payload.append('photo', formData.photo);
            payload.append('conducted_by', user?.name || 'Center Head');

            const res = await api.post('/admissions', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSuccessData({
                student_name: formData.student_name,
                center_name: user?.center_name || formData.center_name,
                ...res.data
            });
            setShowSuccessModal(true);

            // Reset form
            setFormData({
                student_name: '', email: '', batch: '', batch_time: '',
                drcss_no: '', aadhar_no: '', course: 'KYP',
                admission_date: new Date().toISOString().split('T')[0],
                payment_amount: '0', admission_type: 'free',
                center_name: user?.center_name || '',
                center_address: user?.center_address || '',
                photo: null,
            });
            setCapturedImage(null);
            fetchFreeAdmissions();
        } catch (err) {
            console.error(err);
            setMsg(err.response?.data?.message || 'Admission failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ── Update portal status (Pending -> Confirm, then lock) ──
    const handleStatusToggle = async (adm) => {
        if (adm.admin_status === 'Confirm') return; // locked after confirm
        setStatusUpdating(adm.id);
        try {
            await api.put(`/admissions/${adm.id}/admin-status`, { admin_status: 'Confirm' });
            setFreeAdmissions(prev => prev.map(a =>
                a.id === adm.id ? { ...a, admin_status: 'Confirm' } : a
            ));
        } catch (e) {
            console.error(e);
        } finally {
            setStatusUpdating(null);
        }
    };

    // ── Stats ──
    const totalDue = freeAdmissions.reduce((s, a) => s + (Number(a.due_amount) || 1000), 0);
    const confirmedCount = freeAdmissions.filter(a => a.admin_status === 'Confirm').length;
    const filteredList = freeAdmissions.filter(a =>
        !searchTerm ||
        a.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.aadhar_no?.includes(searchTerm)
    );
    const totalPages = Math.ceil(filteredList.length / itemsPerPage);
    const currentItems = filteredList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div style={{ fontFamily: "'Inter', sans-serif" }}>

            {/* ── Global responsive CSS (always rendered regardless of view) ── */}
            <style>{`
                .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; }
                @media (max-width: 900px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
                @media (max-width: 560px) { .form-grid { grid-template-columns: 1fr; } }
                @media (max-width: 600px) {
                    .fa-stats-bar { grid-template-columns: repeat(2, 1fr) !important; }
                    .fa-page-header { flex-wrap: wrap !important; }
                }
            `}</style>

            {/* ── Page Header ── */}
            <div style={{ marginBottom: '1rem' }}>
                <div className="fa-page-header" style={{
                    background: 'linear-gradient(90deg, #3182CE 0%, #2B6CB0 100%)',
                    padding: '0.85rem 1.1rem', color: 'white',
                    borderRadius: cardRadius,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.7rem'
                }}>
                    <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '800', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                        FREE ADMISSION
                    </h2>

                    {/* Toggle Buttons */}
                    <div className="fa-toggle-btns" style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button onClick={() => setActiveView('form')} style={{
                            padding: '0.4rem 0.8rem', borderRadius: '6px', fontWeight: '700',
                            fontSize: '0.78rem', cursor: 'pointer', border: 'none',
                            background: activeView === 'form' ? 'white' : 'rgba(255,255,255,0.15)',
                            color: activeView === 'form' ? '#2B6CB0' : 'white',
                            display: 'flex', alignItems: 'center', gap: '0.3rem'
                        }}>
                            <MdPersonAdd size={14} /> New Admission
                        </button>
                        <button onClick={() => setActiveView('list')} style={{
                            padding: '0.4rem 0.8rem', borderRadius: '6px', fontWeight: '700',
                            fontSize: '0.78rem', cursor: 'pointer', border: 'none',
                            background: activeView === 'list' ? 'white' : 'rgba(255,255,255,0.15)',
                            color: activeView === 'list' ? '#2B6CB0' : 'white',
                            display: 'flex', alignItems: 'center', gap: '0.3rem'
                        }}>
                            <MdListAlt size={14} /> Records ({freeAdmissions.length})
                        </button>
                    </div>
                </div>
            </div>


            {/* ── FORM VIEW ── */}
            <AnimatePresence mode="wait">
                {activeView === 'form' && (
                    <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{
                            background: 'white', borderRadius: cardRadius,
                            border: '1px solid #E2E8F0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
                            marginBottom: '2rem', overflow: 'hidden'
                        }}>


                        <div style={{ padding: '1.5rem' }}>
                            {msg && (
                                <div style={{
                                    marginBottom: '1rem', padding: '0.8rem 1rem',
                                    background: '#FFF5F5', color: '#C53030',
                                    borderRadius: '6px', border: '1px solid #FED7D7',
                                    fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                                }}>
                                    <MdError /> {msg}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="admission-form">
                                <div className="form-grid">
                                    <FormInput label="Student Full Name" name="student_name" placeholder="Rahul Sharma" value={formData.student_name} onChange={e => setFormData(p => ({ ...p, student_name: e.target.value }))} required />
                                    <FormInput label="Student Email" name="email" type="email" placeholder="student@example.com" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} required />

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Batch</label>
                                        <select name="batch" value={formData.batch} onChange={e => setFormData(p => ({ ...p, batch: e.target.value }))}
                                            style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                            <option value="">Select Batch Month</option>
                                            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Course Name</label>
                                        <select name="course" value={formData.course} onChange={e => setFormData(p => ({ ...p, course: e.target.value }))}
                                            style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                            <option value="KYP">KYP</option>
                                        </select>
                                    </div>

                                    {/* Center Name */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Center Name</label>
                                        <div style={{ position: 'relative' }}>
                                            <input type="text" value={formData.center_name}
                                                onChange={e => setFormData(p => ({ ...p, center_name: e.target.value }))}
                                                readOnly={user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name}
                                                placeholder="Enter Center Name"
                                                style={{
                                                    width: '100%', padding: '0.8rem 0.8rem 0.8rem 2.5rem',
                                                    borderRadius: '4px', border: '1px solid #CBD5E0',
                                                    background: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? '#F7FAFC' : '#FFFFFF',
                                                    color: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? '#718096' : '#2D3748',
                                                    cursor: (user?.role !== 'admin' && user?.role !== 'sub_admin' && !!user?.center_name) ? 'not-allowed' : 'text',
                                                    fontWeight: 'bold',
                                                    boxSizing: 'border-box'
                                                }} required />
                                            <MdClass style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#A0AEC0' }} />
                                        </div>
                                    </div>

                                    <FormInput label="Admission Date" name="admission_date" type="date" value={formData.admission_date} onChange={e => setFormData(p => ({ ...p, admission_date: e.target.value }))} required />

                                    {/* Amount Display - Fixed */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Payment Amount</label>
                                        <div style={{
                                            padding: '0.8rem', borderRadius: '4px', border: '1px solid #C6F6D5',
                                            background: '#F0FFF4', color: '#276749', fontWeight: '700', fontSize: '0.9rem',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                        }}>
                                            <span>₹0 (Free)</span>
                                            <span style={{ fontSize: '0.75rem', color: '#C53030', fontWeight: '700' }}>Due: ₹1000</span>
                                        </div>
                                    </div>

                                    <FormInput label="DRCC No." name="drcss_no" placeholder="Reg No." value={formData.drcss_no} onChange={e => setFormData(p => ({ ...p, drcss_no: e.target.value }))} />
                                    <FormInput label="Aadhar Number" name="aadhar_no" placeholder="12 Digit No." value={formData.aadhar_no} onChange={e => setFormData(p => ({ ...p, aadhar_no: e.target.value }))} />

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>Batch Time</label>
                                        <select name="batch_time" value={formData.batch_time} onChange={e => setFormData(p => ({ ...p, batch_time: e.target.value }))}
                                            style={{ padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', width: '100%', outline: 'none', fontSize: '0.9rem', background: '#FFFFFF' }} required>
                                            <option value="">Select Time</option>
                                            {['6AM TO 10AM', '7AM TO 11AM', '10AM TO 2PM', '11AM TO 3PM', '2PM TO 6PM', '3PM TO 7PM'].map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Photo Capture (same as existing admission form) ── */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '1.5rem' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>
                                        Student Photo <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <div style={{ background: '#F7FAFC', padding: '1rem', borderRadius: '8px', border: '1px dashed #CBD5E0', textAlign: 'center' }}>
                                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                                        {!isCameraOpen && !capturedImage && !cameraError && (
                                            <div style={{ padding: '1rem' }}>
                                                <div style={{ marginBottom: '1rem', color: '#718096', fontSize: '0.9rem' }}>Capture live photo using camera</div>
                                                <button type="button" onClick={startCamera}
                                                    style={{ background: '#3182CE', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 auto', fontWeight: 'bold' }}>
                                                    <MdAddCircle /> Open Camera
                                                </button>
                                                <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#A0AEC0' }}>or</div>
                                                <button type="button" onClick={() => setCameraError(true)}
                                                    style={{ background: 'transparent', border: 'none', textDecoration: 'underline', color: '#718096', cursor: 'pointer', fontSize: '0.8rem' }}>
                                                    Issues with camera? Upload File
                                                </button>
                                            </div>
                                        )}

                                        {isCameraOpen && (
                                            <div style={{ position: 'relative', width: '100%', maxWidth: '320px', margin: '0 auto' }}>
                                                <video ref={videoRef} style={{ width: '100%', borderRadius: '8px', background: 'black' }} muted playsInline />
                                                <button type="button" onClick={switchCamera}
                                                    style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                                                    <MdFlipCameraIos size={20} />
                                                </button>
                                                <div style={{ marginTop: '0.8rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                                    <button type="button" onClick={stopCamera} style={{ background: '#E53E3E', color: 'white', border: 'none', padding: '0.6rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                                    <button type="button" onClick={capturePhoto} style={{ background: '#38A169', color: 'white', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Click Photo</button>
                                                </div>
                                            </div>
                                        )}

                                        {capturedImage && (
                                            <div style={{ textAlign: 'center' }}>
                                                <img src={capturedImage} alt="Captured" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #3182CE', marginBottom: '1rem' }} />
                                                <div>
                                                    <button type="button" onClick={retakePhoto}
                                                        style={{ background: 'white', border: '1px solid #CBD5E0', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', color: '#4A5568', fontSize: '0.85rem' }}>
                                                        Retake Photo
                                                    </button>
                                                </div>
                                                <div style={{ color: '#38A169', fontWeight: 'bold', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                                                    <MdCheckCircle /> Photo Captured
                                                </div>
                                            </div>
                                        )}

                                        {cameraError && (
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ marginBottom: '0.5rem', color: '#E53E3E', fontSize: '0.85rem' }}>Camera unavailable. Upload file instead.</div>
                                                <input type="file" accept="image/*"
                                                    onChange={e => {
                                                        if (e.target.files[0]) {
                                                            setFormData(p => ({ ...p, photo: e.target.files[0] }));
                                                            setCapturedImage(URL.createObjectURL(e.target.files[0]));
                                                            setCameraError(false);
                                                        }
                                                    }}
                                                    style={{ padding: '0.6rem', border: '1px solid #CBD5E0', borderRadius: '4px', width: '100%' }}
                                                    required={!formData.photo}
                                                />
                                                <button type="button" onClick={() => setCameraError(false)}
                                                    style={{ marginTop: '0.8rem', background: 'transparent', border: 'none', textDecoration: 'underline', color: '#3182CE', cursor: 'pointer', fontSize: '0.8rem' }}>
                                                    Try Camera Again
                                                </button>
                                            </div>
                                        )}

                                        <input tabIndex={-1} autoComplete="off"
                                            style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }}
                                            required={!formData.photo}
                                            onInvalid={e => e.target.setCustomValidity('Please capture or upload a student photo')}
                                            onInput={e => e.target.setCustomValidity('')}
                                        />
                                    </div>
                                </div>

                                {/* Permanent Address */}
                                <div style={{ marginTop: '1.5rem' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568', marginBottom: '0.4rem', display: 'block' }}>Permanent Address</label>
                                    <textarea
                                        name="center_address"
                                        value={formData.center_address}
                                        onChange={e => setFormData(p => ({ ...p, center_address: e.target.value }))}
                                        readOnly={!!user?.center_address}
                                        placeholder="Complete Address..."
                                        style={{
                                            width: '100%', padding: '0.8rem', borderRadius: '4px',
                                            border: '1px solid #CBD5E0', minHeight: '80px',
                                            fontFamily: 'inherit', fontSize: '0.9rem',
                                            background: user?.center_address ? '#F7FAFC' : 'white',
                                            boxSizing: 'border-box', resize: 'vertical'
                                        }}
                                        required
                                    />
                                </div>

                                {/* Submit */}
                                <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                                    <button type="submit" disabled={loading} style={{
                                        padding: '0.9rem 3rem', background: '#319795', color: 'white',
                                        border: 'none', borderRadius: '6px', fontWeight: '600',
                                        fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 4px 6px rgba(49,151,149,0.4)',
                                        opacity: loading ? 0.8 : 1
                                    }}>
                                        {loading ? 'Processing...' : 'Submit Free Admission'}
                                    </button>
                                </div>
                            </form>

                            <style>{`
                                .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; }
                                @media (max-width: 900px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
                                @media (max-width: 560px) { .form-grid { grid-template-columns: 1fr; } }
                                @media (max-width: 560px) { .fa-stats-bar { grid-template-columns: repeat(2, 1fr) !important; } }
                            `}</style>
                        </div>
                    </motion.div>
                )}

                {/* ── LIST VIEW ── */}
                {activeView === 'list' && (
                    <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ background: 'white', borderRadius: cardRadius, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>

                        {/* List Header */}
                        <div style={{
                            background: 'linear-gradient(90deg, #3182CE 0%, #2B6CB0 100%)',
                            padding: '0.6rem 1rem', display: 'flex',
                            alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem',
                            borderRadius: `${cardRadius} ${cardRadius} 0 0`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', flexShrink: 0 }}>
                                <MdListAlt size={17} />
                                <span style={{ fontWeight: '700', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Free Admissions</span>
                                <span style={{ background: 'rgba(255,255,255,0.25)', padding: '1px 7px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: '700' }}>
                                    {freeAdmissions.length}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
                                <div style={{ position: 'relative' }}>
                                    <MdSearch style={{ position: 'absolute', left: '7px', top: '50%', transform: 'translateY(-50%)', color: '#A0AEC0' }} size={13} />
                                    <input
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        style={{ paddingLeft: '24px', paddingRight: '6px', paddingTop: '0.32rem', paddingBottom: '0.32rem', border: 'none', borderRadius: '4px', fontSize: '0.78rem', outline: 'none', width: '120px' }}
                                    />
                                </div>
                                <button onClick={fetchFreeAdmissions} title="Refresh"
                                    style={{ padding: '0.32rem', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'white' }}>
                                    <MdRefresh size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Stats Bar — 4 cards, 2x2 on mobile */}
                        <div className="fa-stats-bar" style={{
                            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                            borderBottom: '1px solid #E2E8F0'
                        }}>
                            <StatBar icon={<MdGroup size={15} color="#553C9A" />} label="Total" value={freeAdmissions.length} bg="#FAF5FF" color="#553C9A" />
                            <StatBar icon={<MdCurrencyRupee size={15} color="#C53030" />} label="Pending Fees" value={`Rs.${totalDue.toLocaleString()}`} bg="#FFF5F5" color="#C53030" border />
                            <StatBar icon={<MdCheckCircle size={15} color="#276749" />} label="Confirmed" value={confirmedCount} bg="#F0FFF4" color="#276749" border />
                            <StatBar icon={<MdPerson size={15} color="#2B6CB0" />} label="Center" value={user?.center_name || '—'} bg="#EBF8FF" color="#2B6CB0" border />
                        </div>

                        {fetchLoading ? (
                            <div style={{ padding: '3rem', textAlign: 'center', color: '#A0AEC0' }}>Loading...</div>
                        ) : filteredList.length === 0 ? (
                            <div style={{ padding: '3rem', textAlign: 'center', color: '#A0AEC0' }}>
                                {searchTerm ? 'No student found matching your search.' : 'No free admissions yet. Click "New Admission" to get started.'}
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                                        <thead>
                                            <tr>
                                                <Th>#</Th>
                                                <Th>Student Name</Th>
                                                <Th>Reg No.</Th>
                                                <Th>Course / Batch</Th>
                                                <Th>Date</Th>
                                                <Th>Due Amt.</Th>
                                                <Th>Portal Status</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.map((adm, i) => (
                                                <tr key={adm.id}
                                                    onClick={() => setSelectedStudent(adm)}
                                                    style={{ background: adm.admin_status === 'Confirm' ? '#F0FFF4' : 'white', transition: 'background 0.2s', cursor: 'pointer' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#E6FFFA'}
                                                    onMouseLeave={e => e.currentTarget.style.background = adm.admin_status === 'Confirm' ? '#F0FFF4' : 'white'}
                                                >
                                                    <Td>{(currentPage - 1) * itemsPerPage + i + 1}</Td>

                                                    {/* Student name with photo avatar */}
                                                    <Td style={{ fontWeight: '600', color: '#2D3748', whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                                                            <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#EBF8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#2B6CB0', fontSize: '0.75rem', flexShrink: 0, overflow: 'hidden', border: '1px solid #BEE3F8' }}>
                                                                {adm.photo_url ? (
                                                                    <img src={adm.photo_url} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                ) : (
                                                                    adm.student_name?.charAt(0)?.toUpperCase()
                                                                )}
                                                            </div>
                                                            {adm.student_name}
                                                        </div>
                                                    </Td>

                                                    <Td>
                                                        {adm.registration_no
                                                            ? <span style={{ fontWeight: '600', color: '#2D3748', whiteSpace: 'nowrap' }}>{adm.registration_no}</span>
                                                            : <span style={{ padding: '1px 6px', background: '#FEFCBF', color: '#B7791F', borderRadius: '3px', fontSize: '0.72rem', fontWeight: '600' }}>Processing</span>
                                                        }
                                                    </Td>

                                                    {/* Course + Batch on same line */}
                                                    <Td style={{ whiteSpace: 'nowrap' }}>
                                                        <span style={{ fontWeight: '600', color: '#4A5568' }}>{adm.course}</span>
                                                        <span style={{ color: '#A0AEC0', fontSize: '0.73rem', marginLeft: '0.3rem' }}>{adm.batch}</span>
                                                    </Td>

                                                    <Td style={{ whiteSpace: 'nowrap' }}>{adm.admission_date}</Td>

                                                    <Td>
                                                        <span style={{ padding: '1px 6px', background: '#FED7D7', color: '#C53030', borderRadius: '3px', fontSize: '0.76rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                                            Rs. {Number(adm.due_amount) || 1000}
                                                        </span>
                                                    </Td>

                                                    <Td>
                                                        {adm.admin_status === 'Confirm' ? (
                                                            // LOCKED after confirm
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '1px 7px', background: '#C6F6D5', color: '#276749', borderRadius: '3px', fontSize: '0.71rem', fontWeight: '700', width: 'fit-content' }}>
                                                                <MdLock size={11} /> Confirmed
                                                            </span>
                                                        ) : (
                                                            // Clickable to confirm
                                                            <button
                                                                onClick={() => handleStatusToggle(adm)}
                                                                disabled={statusUpdating === adm.id}
                                                                title="Click to mark as Confirmed for Govt Portal"
                                                                style={{
                                                                    padding: '2px 8px', background: '#EDF2F7', color: '#4A5568',
                                                                    border: '1px solid #CBD5E0', borderRadius: '3px',
                                                                    fontSize: '0.71rem', fontWeight: '600', cursor: 'pointer',
                                                                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                                                                    opacity: statusUpdating === adm.id ? 0.6 : 1
                                                                }}>
                                                                <MdCheckCircle size={11} />
                                                                {statusUpdating === adm.id ? '...' : 'Pending'}
                                                            </button>
                                                        )}
                                                    </Td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div style={{ padding: '0.9rem 1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                                        <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                                            style={{ padding: '0.4rem 0.7rem', border: '1px solid #CBD5E0', borderRadius: '4px', background: currentPage === 1 ? '#EDF2F7' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: '#4A5568' }}>
                                            <MdChevronLeft />
                                        </button>
                                        <span style={{ fontSize: '0.85rem', color: '#4A5568', fontWeight: '600' }}>Page {currentPage} of {totalPages}</span>
                                        <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                                            style={{ padding: '0.4rem 0.7rem', border: '1px solid #CBD5E0', borderRadius: '4px', background: currentPage === totalPages ? '#EDF2F7' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: '#4A5568' }}>
                                            <MdChevronRight />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── SUCCESS MODAL (same style as existing app) ── */}
            <AnimatePresence>
                {showSuccessModal && successData && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
                        <motion.div
                            initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
                            style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>

                            <div style={{ width: '60px', height: '60px', background: '#F0FFF4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                                <MdCheckCircle size={36} color="#38A169" />
                            </div>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1A202C', marginBottom: '0.3rem' }}>Free Admission Successful!</h2>
                            <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                                Student has been admitted. Collect Rs.1000 later.
                            </p>

                            <div style={{ background: '#F7FAFC', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', textAlign: 'left' }}>
                                <InfoRow label="Student" value={successData.student_name} />
                                <InfoRow label="Center" value={successData.center_name} />
                                <InfoRow label="Pending Amount" value="Rs. 1000" color="#C53030" />
                            </div>

                            <div style={{ display: 'flex', gap: '0.8rem' }}>
                                <button onClick={() => { setShowSuccessModal(false); setActiveView('form'); }}
                                    style={{ flex: 1, padding: '0.9rem', background: '#319795', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}>
                                    + Add Another
                                </button>
                                <button onClick={() => { setShowSuccessModal(false); setActiveView('list'); }}
                                    style={{ flex: 1, padding: '0.9rem', background: '#EDF2F7', color: '#2D3748', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}>
                                    View Records
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── Student Details Modal ── */}
            {selectedStudent && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: '1rem', backdropFilter: 'blur(3px)'
                }} onClick={() => setSelectedStudent(null)}>
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        style={{
                            background: 'white', borderRadius: '12px', width: '100%',
                            maxWidth: '380px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                        }} onClick={e => e.stopPropagation()}
                    >
                        <div style={{ background: '#2B6CB0', padding: '1rem', color: 'white', position: 'relative', textAlign: 'center' }}>
                            <button onClick={() => setSelectedStudent(null)} style={{ position: 'absolute', right: '10px', top: '10px', border: 'none', background: 'none', color: 'white', cursor: 'pointer' }}>
                                <MdClose size={18} />
                            </button>
                            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'white', border: '2px solid #63B3ED', margin: '0 auto 0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {selectedStudent.photo_url ? (
                                    <img src={selectedStudent.photo_url} alt="student" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <MdPerson size={36} color="#2B6CB0" />
                                )}
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '800' }}>{selectedStudent.student_name}</h3>
                            <p style={{ margin: '2px 0 0', opacity: 0.9, fontSize: '0.75rem', fontWeight: '600' }}>{selectedStudent.registration_no || 'Registration Pending'}</p>
                        </div>

                        <div style={{ padding: '0.9rem', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.5rem', maxHeight: '55vh', overflowY: 'auto' }}>
                            <div style={{ gridColumn: 'span 2' }}>
                                <DetailItem label="Email Address" value={selectedStudent.email} icon={<MdEmail size={13} />} />
                            </div>
                            <DetailItem label="Course" value={selectedStudent.course} icon={<MdSchool size={13} />} />
                            <DetailItem label="Batch" value={selectedStudent.batch} icon={<MdCalendarToday size={13} />} />
                            <DetailItem label="Batch Time" value={selectedStudent.batch_time} icon={<MdAccessTime size={13} />} />
                            <DetailItem label="Aadhar No." value={selectedStudent.aadhar_no} icon={<MdFingerprint size={13} />} />
                            <DetailItem label="DRCSS No." value={selectedStudent.drcss_no} icon={<MdInfo size={13} />} />
                            <DetailItem label="Admission Date" value={selectedStudent.admission_date} icon={<MdToday size={13} />} />

                            <div style={{ gridColumn: 'span 2', marginTop: '0.4rem', padding: '0.7rem', borderRadius: '8px', background: selectedStudent.admin_status === 'Confirm' ? '#F0FFF4' : '#FFFBEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid', borderColor: selectedStudent.admin_status === 'Confirm' ? '#C6F6D5' : '#FEFCBF' }}>
                                <span style={{ fontSize: '0.65rem', fontWeight: '700', color: selectedStudent.admin_status === 'Confirm' ? '#276749' : '#975A16' }}>PORTAL STATUS</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: '800', color: selectedStudent.admin_status === 'Confirm' ? '#2F855A' : '#B7791F', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                    {selectedStudent.admin_status === 'Confirm' ? <MdCheckCircle size={13} /> : <MdWarning size={13} />}
                                    {selectedStudent.admin_status || 'Pending'}
                                </span>
                            </div>
                        </div>

                        <div style={{ padding: '0.8rem', borderTop: '1px solid #F7FAFC', display: 'flex', justifyContent: 'center' }}>
                            <button onClick={() => setSelectedStudent(null)} style={{ width: '100%', padding: '0.6rem', background: '#F7FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', color: '#4A5568', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer' }}>Close Details</button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

// ── Small helper for details modal rows ──
function DetailItem({ label, value, icon }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', borderBottom: '1px solid #F7FAFC', paddingBottom: '0.4rem' }}>
            <div style={{ color: '#A0AEC0', display: 'flex', alignItems: 'center' }}>{icon}</div>
            <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '0.65rem', color: '#A0AEC0', fontWeight: '700', textTransform: 'uppercase' }}>{label}</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#2D3748', fontWeight: '600' }}>{value || '—'}</p>
            </div>
        </div>
    );
}

// ── Small helpers ──
function StatBar({ icon, label, value, bg, color, border }) {
    return (
        <div style={{
            padding: '0.9rem 1.2rem', background: bg, display: 'flex', alignItems: 'center', gap: '0.7rem',
            borderLeft: border ? '1px solid #E2E8F0' : 'none'
        }}>
            <div style={{ flexShrink: 0 }}>{icon}</div>
            <div>
                <div style={{ fontSize: '0.7rem', color: '#718096', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: '1rem', fontWeight: '800', color }}>{value}</div>
            </div>
        </div>
    );
}

function InfoRow({ label, value, color }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ color: '#718096', fontSize: '0.85rem' }}>{label}</span>
            <span style={{ fontWeight: '700', color: color || '#2D3748', fontSize: '0.85rem' }}>{value}</span>
        </div>
    );
}
